import { Injectable, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HandleHttpError } from './error.handler';
import { StudentDetails } from 'src/app/application/models/student-details';
import { ParentGuardianDetails, ParentLegalcustodianDetails } from 'src/app/application/models/parent-guardian-details';
import { IApplicationStep } from 'src/app/application/models/application-step.interface';
import { VisaOshc } from 'src/app/application/models/visa-ohsc';
import { EnglishProficiencyDetails } from 'src/app/application/models/english-proficiency-details';
import { SchoolProgramPlacementDetails } from 'src/app/application/models/school-program-placement-details';
import { AustralianState } from './australian-states.model';
import { SpecialCircumstances } from 'src/app/application/models/special-circumstances';
import { DefaultCorrespondent } from 'src/app/application/models/corresondents';
import { SharedService } from './shared.service';
import { AccommodationWelfareUpdateDetails } from 'src/app/application/models/accommodation-welfare-details';
import { ApplicationPayment } from 'src/app/application/models/application-payment';
import { UploadedSupportingDocument } from 'src/app/application/models/supporting-document';
import { PreRegistrationModel } from '../models/pre-registration.model';
import { ApplicationAgentDetails } from 'src/app/application/models/application-agent-details';
import { SingleResultApiResponse } from '../models/api-response.model';


@Injectable()
export class ApplicationService implements OnInit {

    constructor(private http: HttpClient, private sharedService: SharedService) {
        this.baseURL = `${this.sharedService.getBaseURL()}/applications`;
        this.voucherURL = `${this.sharedService.getBaseURL()}/voucher`;
    }

    baseURL: string;
    voucherURL: string;

    applicationSteps: IApplicationStep[] = [
        {
            Id: 1,
            Description: 'Important Information',
            URL: 'important-information',
            Section: '',
            Status: ''
        },
        {
            Id: 2,
            Description: 'Student Details',
            URL: 'student-details',
            Section: 'StudentDetails',
            Status: ''
        },
        {
            Id: 3,
            Description: 'English Proficiency',
            URL: 'english-proficiency',
            Section: 'EnglishProficiency',
            Status: ''
        },
        {
            Id: 4,
            Description: 'School Program & Placement',
            URL: 'school-program-placement',
            Section: 'SchoolProgramAndPlacement',
            Status: ''
        },
        {
            Id: 5,
            Description: 'Parent / Carer Details',
            URL: 'parent-guardian-details',
            //URL: 'parent-guardian-details',
            Section: 'ParentGuardianDetails',
            Status: ''
        },
        {
            Id: 6,
            Description: 'Accommodation & Welfare',
            URL: 'accommodation-welfare',
            Section: 'AccommodationAndWelfare',
            Status: ''
        },
        {
            Id: 7,
            Description: 'VISA and OSHC',
            URL: 'visa-oshc',
            Section: 'VisaAndOhscDetails',
            Status: ''
        },
        {
            Id: 8,
            Description: 'Special Circumstances',
            URL: 'special-circumstances',
            Section: 'SpecialCircumstances',
            Status: ''
        },
        {
            Id: 9,
            Description: 'Supporting Documents',
            URL: 'supporting-documents',
            Section: 'SupportingDocuments',
            Status: ''
        },
        {
            Id: 10,
            Description: 'Summary',
            URL: 'summary',
            Section: 'Summary',
            Status: ''
        },
        {
            Id: 15,
            Description: 'Agent Details',
            URL: 'agent-details',
            Section: 'AgentDetails',
            Status: ''
        },
        {
            Id: 12,
            Description: 'Payment',
            URL: 'payment',
            Section: 'MakePayment',
            Status: ''
        },
    ];

    ngOnInit() {
    }

    getApplicationSteps(): IApplicationStep[] {
        return this.applicationSteps;
    }

    getApplicationStatuses(): Observable<any> {
        return this.http.get(`${this.baseURL}/status`)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    getApplicationStatusesBySearchTerm(searchTerm: string): Observable<any> {
        return this.http.get(`${this.baseURL}/status/search/${searchTerm}`)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    getAustralianStates(): AustralianState[] {
        return [
            {
                Code: 'NSW',
                Description: 'New South Wales'
            },
            {
                Code: 'QLD',
                Description: 'Queensland'
            },
            {
                Code: 'SA',
                Description: 'South Australia'
            },
            {
                Code: 'TAS',
                Description: 'Tasmania'
            },
            {
                Code: 'VIC',
                Description: 'Victoria'
            },
            {
                Code: 'WA',
                Description: 'Western Australia'
            }
        ];
    }

    getApplicationAgentDetails(applnId: number): Observable<any> {
        return this.http.get(`${this.baseURL}/${applnId}/agent-details`)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    updateApplicationAgentDetails(applicationId: number, agentDetails: ApplicationAgentDetails): Observable<any> {
        return this.http.post(`${this.baseURL}/${applicationId}/agent-details`, agentDetails)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    getApplicationDetails(applnId: number): Observable<any> {
        return this.http.get(`${this.baseURL}/${applnId}`)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    getApplicationSections(applnId: number): Observable<any> {
        return this.http.get(`${this.baseURL}/${applnId}/sections`)
            .pipe(
                map((response: any) => {
                    return {
                        ...response, 
                        Result: response.Result
                                    .filter(x => x.Type !== 'VisaProgramTriage')
                                    .map(x => ({...x, Type: x.Type === 'RiskAssessment' ? 'SpecialCircumstances' : x.Type}))
                    }
                }),
                catchError(err => HandleHttpError(err))
            );
    }

    createApplication(): Observable<any> {
        return this.http.post(`${this.baseURL}`, null)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    createApplicationByAgent(preRegistrationDetails: PreRegistrationModel): Observable<any> {
        return this.http.post(`${this.baseURL}/create-application`, preRegistrationDetails)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    deleteApplication(applicationId): Observable<any> {
        return this.http.delete(`${this.baseURL}/${applicationId}`)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    cloneApplication(applicationId): Observable<any> {
        return this.http.post(`${this.baseURL}/${applicationId}/clone`, applicationId)
            .pipe(catchError(err => HandleHttpError(err)));
    }

  cloneSAApplication(applicationId): Observable<any> {
    return this.http.post(`${this.baseURL}/${applicationId}/clone`, applicationId)
      .pipe(catchError(err => HandleHttpError(err)));  
  }

    cloneSiblingApplication(applicationId: string, preRegistrationDetails: PreRegistrationModel): Observable<any> {
        return this.http.post(`${this.baseURL}/cloneSibling?applicationId=${applicationId}`, preRegistrationDetails)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    getStudentDetails(applicationId: number): Observable<any> {
        return this.http.get(`${this.baseURL}/${applicationId}/details`)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    updateStudentDetails(applicationId: number, studentDetails: StudentDetails): Observable<any> {
        return this.http.post(`${this.baseURL}/${applicationId}/details`, studentDetails)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    getParentGuardiansDetails(applicationId: number): Observable<any> {
        return this.http.get(`${this.baseURL}/${applicationId}/contacts`)
            .pipe(catchError(err => HandleHttpError(err)));
    }


    getParentGuardianDetails(applicationId: number, parentGuardianId): Observable<any> {
        return this.http.get(`${this.baseURL}/${applicationId}/contacts/${parentGuardianId}`)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    getParentGuardianReferences(applicationId: number, parentGuardianId): Observable<any> {
        return this.http.get(`${this.baseURL}/${applicationId}/contacts/${parentGuardianId}/references`)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    updateParentGuardianDetails(applicationId: number, parentGuardianDetails: ParentGuardianDetails): Observable<any> {

        const { ParentGuardianId } = parentGuardianDetails;
        if (ParentGuardianId === 0) {
            return this.http.post(`${this.baseURL}/${applicationId}/contacts`, parentGuardianDetails)
                .pipe(catchError(err => HandleHttpError(err)));
        } else {
            return this.http.post(`${this.baseURL}/${applicationId}/contacts/${ParentGuardianId}`, parentGuardianDetails)
                .pipe(catchError(err => HandleHttpError(err)));
        }
    }

    getParentLegalcustodian(applicationId: number): Observable<any> {
        return this.http.get(`${this.baseURL}/${applicationId}/parent-legalcustodian`)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    updateParentLegalcustodian(applicationId: number, parentLegalcustodianDetails: ParentLegalcustodianDetails): Observable<any> {
        const { P1ParentId, P2ParentId, LCParentId, IsParentTheCarer } = parentLegalcustodianDetails;
        //console.log('application.service.ts - updateParentLegalcustodian');
        //console.log(parentLegalcustodianDetails);
        if ((IsParentTheCarer && !P1ParentId && !P2ParentId) ||
            (!IsParentTheCarer && !P1ParentId && !P2ParentId && !LCParentId)) {
            return this.http.post(`${this.baseURL}/${applicationId}/parent-legalcustodian`, parentLegalcustodianDetails)
                .pipe(catchError(err => HandleHttpError(err)));
        } else {
            return this.http.post(`${this.baseURL}/${applicationId}/parent-legalcustodian-update`, parentLegalcustodianDetails)
                .pipe(catchError(err => HandleHttpError(err)));
        }
    }
    
    IsParentOrLegalcustodian(applicationId: number, parentGuardianId: number): Observable<any> {
        return this.http.get(`${this.baseURL}/${applicationId}/is-parent-or-legalcustodian/${parentGuardianId}`)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    deleteParentGuardianDetails(applicationId: number, parentGuardianId: number): Observable<any> {
        return this.http.delete(`${this.baseURL}/${applicationId}/contacts/${parentGuardianId}`)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    getVisaOhscDetails(applicationId: number): Observable<any> {
        return this.http.get(`${this.baseURL}/${applicationId}/visaoshc`)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    updateVisaOhscDetails(applicationId: number, visaOshcDetails: VisaOshc): Observable<any> {
        return this.http.post(`${this.baseURL}/${applicationId}/visaoshc`, visaOshcDetails)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    getEnglishProficiencyDetails(applicationId: number): Observable<any> {
        return this.http.get(`${this.baseURL}/${applicationId}/englishproficiency`)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    updateEnglishProficiencyDetails(applicationId: number, englishProficiencyDetails: EnglishProficiencyDetails): Observable<any> {
        return this.http.post(`${this.baseURL}/${applicationId}/englishproficiency`, englishProficiencyDetails)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    getSchoolProgramAndPlacementDetails(applicationId: number): Observable<any> {
        return this.http.get(`${this.baseURL}/${applicationId}/program`)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    getSchoolProgramAndPlacementPackage(applicationId: number): Observable<any> {
        return this.http.get(`${this.baseURL}/${applicationId}/program-package`)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    updateSchoolProgramAndPlacementDetails(applicationId: number, programDetails: SchoolProgramPlacementDetails): Observable<any> {
        return this.http.post(`${this.baseURL}/${applicationId}/program`, programDetails)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    getSpecialCircumstances(applicationId: number): Observable<any> {
        return this.http.get(`${this.baseURL}/${applicationId}/specialcircumstances`)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    updateSpecialCircumstances(applicationId: number, specialCircumstances: SpecialCircumstances): Observable<any> {
        return this.http.post(`${this.baseURL}/${applicationId}/specialcircumstances`, specialCircumstances)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    getAllCorrespondents(applicationId: number): Observable<any> {
        return this.http.get(`${this.baseURL}/${applicationId}/correspondents`)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    getAccommodationWelfareDetails(applicationId: number): Observable<any> {
        return this.http.get(`${this.baseURL}/${applicationId}/accommodation`)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    updateAccommodationWelfareDetails(applicationId: number,
        accommodationWelfareUpdateDetails: AccommodationWelfareUpdateDetails): Observable<any> {
        return this.http.post(`${this.baseURL}/${applicationId}/accommodation`, accommodationWelfareUpdateDetails)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    setDefaultCorrespondent(applicationId: number, defaultCorrespondent: DefaultCorrespondent): Observable<any> {
        return this.http.post(`${this.baseURL}/${applicationId}/correspondents/set-default`, defaultCorrespondent)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    getSupportingDocuments(applicationId: number): Observable<any> {
        return this.http.get(`${this.baseURL}/${applicationId}/documents`)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    getSupportingDocument(applicationId: number, documentTypeId): Observable<any> {
        return this.http.get(`${this.baseURL}/${applicationId}/documents/document/${documentTypeId}`)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    saveSupportingDocument(applicationId: number, uploadedSupportingDocument: UploadedSupportingDocument): Observable<any> {
        return this.http.post(`${this.baseURL}/${applicationId}/documents`, uploadedSupportingDocument)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    updateSupportingDocumentScanStatus(applicationId: number, documentId): Observable<any> {
        return this.http.post(`${this.baseURL}/${applicationId}/documents/scanstatus/${documentId}`, {})
            .pipe(catchError(err => HandleHttpError(err)));
    }

    updateAllSupportingDocumentsScanStatus(applicationId: Number): Observable<any> {
        return this.http.post(`${this.baseURL}/${applicationId}/documents/scanstatus`, {})
            .pipe(catchError(err => HandleHttpError(err)));
    }

    deleteSupportingDocument(applicationId: number, documentId: number): Observable<any> {
        return this.http.delete(`${this.baseURL}/${applicationId}/documents/${documentId}`)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    saveSupportingDocuments(applicationId: number): Observable<any> {
        return this.http.post(`${this.baseURL}/${applicationId}/documents/save`, {})
            .pipe(catchError(err => HandleHttpError(err)));
    }

    getSummary(applicationId: number): Observable<any> {
        return this.http.get(`${this.baseURL}/${applicationId}/summary`)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    getSummaryWithSupportingDocuments(applicationId: number): Observable<any> {
        return this.http.get(`${this.baseURL}/${applicationId}/summary-with-documents`)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    areAgeAndProgramSelectionStillValid(applicationId: number): Observable<any> {
        return this.http.get(`${this.baseURL}/${applicationId}/are-age-and-term-selection-still-valid`)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    getApplicationPaymentDetails(applicationId: number): Observable<any> {
        return this.http.get(`${this.baseURL}/${applicationId}/payment`)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    makeApplicationPaymentDetails(applicationId: number, voucherNumber: string): Observable<any> {
        if (voucherNumber === null || voucherNumber === undefined || voucherNumber === '') {
            return this.http.post(`${this.baseURL}/${applicationId}/payment`, {})
                .pipe(catchError(err => HandleHttpError(err)));
        }
        else {
            return this.http.post(`${this.baseURL}/${applicationId}/payment?voucherNumber=${voucherNumber}`, {})
                .pipe(catchError(err => HandleHttpError(err)));
        }
    }

    validateVoucher(applicationId: number, voucherNumber: string): Observable<any> {
        return this.http.post(`${this.voucherURL}/validate?applicationId=${applicationId}&voucherNumber=${voucherNumber}`, {})
            .pipe(catchError(err => HandleHttpError(err)));
    }

    getParent(applicationId: number, relationshipCode: string): Observable<any> {
        return this.http.get(`${this.baseURL}/${applicationId}/relationships/${relationshipCode}`)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    validateAddContact(applicationId: number): Observable<any> {
        return this.http.get(`${this.baseURL}/${applicationId}/contacts/validate/add`)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    validateContacts(applicationId: number): Observable<any> {
        return this.http.get(`${this.baseURL}/${applicationId}/contacts/validate`)
            .pipe(catchError(err => HandleHttpError(err)));
    }

    validateVisaOSHC(applicationId: number): Observable<any> {
        return this.http.post(`${this.baseURL}/${applicationId}/validate-visaoshc`, {})
            .pipe(catchError(err => HandleHttpError(err)));
    }

    validateProgram(applicationId: number): Observable<any> {
        return this.http.post(`${this.baseURL}/${applicationId}/validate-program`, {})
            .pipe(catchError(err => HandleHttpError(err)));
    }


    processDataCopyAsync(applicationId: number): Observable<any> {

        var internalDataCopyApiUrl = this.baseURL + `/processDataCopy?applicationId=${applicationId}`;

        console.log("internal api URL fordata-copy is: " + internalDataCopyApiUrl);

        return this.http.post(internalDataCopyApiUrl, {}).pipe(catchError(err => HandleHttpError(err)));
    }

    isCovid19VaccinationNeeded(applicationId: number): Observable<boolean> {
        return this.http.get<SingleResultApiResponse>(`${this.baseURL}/${applicationId}/is-covid19-vaccination-needed`)
            .pipe(
                catchError(err => {
                    debugger;
                    return HandleHttpError(err)
                }),
                map(x => !!(x as any).Result)
            );
        
    }
}
